import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject, signal, TemplateRef } from '@angular/core';
import { UpdatePageTitle } from '../../../store/app/app.actions';
import { Store } from '@ngxs/store';
import { NgxDatatableModule, TableColumn } from '@swimlane/ngx-datatable';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { AppTableComponent } from '../../../shared/components/table/app-table.component';
import { Observable, Subscription } from 'rxjs';
import { SubscriptionApiService } from '../../../_services/subscription-service/api/subscription-api-service.service';
import { HttpParams } from '@angular/common/http';
import { PaginatedResults } from '@bo/ng-api';
import {
  CurrentSubscription,
  SubscriptionDetail,
  SubscriptionFrequency,
  SubscriptionInvoice,
  SubscriptionStatus,
} from '../../../_interfaces/subscription.interface';
import { EmptyIndicatorPipe } from '../../../shared/pipes/empty-indicator.pipe';
import { invoicesTableColumns } from '../../../_services/subscription-service/subscription-plan-utils';
import { OffcanvasService } from '../../../_services/offcanvas-service/offcanvas.service';
import { SetSubscriptionStateUpgradeSubscription } from '../../../store/subscription/subscription.actions';
import { AppSelectors } from '../../../store/app/app.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { InfoBlockComponent } from '../../../shared/components/info-card-block/info-block.component';
import { ToastService } from '../../../_services/toast-service/toast.service';

@Component({
  selector: 'app-your-plan',
  imports: [
    NgxDatatableModule,
    RouterOutlet,
    NgClass,
    AppTableComponent,
    RouterLink,
    DatePipe,
    EmptyIndicatorPipe,
    CurrencyPipe,
    InfoBlockComponent,
  ],
  templateUrl: './your-plan.component.html',
})
export class YourPlanComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly store = inject(Store);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly subscriptionApiService = inject(SubscriptionApiService);
  readonly offcanvasService = inject(OffcanvasService);
  readonly toastService = inject(ToastService);

  readonly businessDetails$ = this.store.select(AppSelectors.businessDetails);
  readonly businessDetails = toSignal(this.businessDetails$, { initialValue: undefined });

  protected currentSubscription?: CurrentSubscription;
  columns: Array<TableColumn> = [];
  protected queryParams = new HttpParams();
  readonly subscriptions: Subscription = new Subscription();
  protected readonly SubscriptionFrequency = SubscriptionFrequency;
  protected upgradesAvailable: SubscriptionDetail[] = [];
  protected nextUpgradeTitle: string = '';
  private readonly columnsInitialized = signal(false);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('dateCell', { static: false }) dateCell!: TemplateRef<any>;
  @ViewChild('amountCell', { static: false }) amountCell!: TemplateRef<any>;
  @ViewChild('actionsCell', { static: false }) actionsCell!: TemplateRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Your plan'));
    this.store.dispatch(new SetSubscriptionStateUpgradeSubscription());
    this.offcanvasService.closeAfterNavigationEnd();
  }

  ngOnInit() {
    // Initialise columns early to prevent expression changed error
    this.columns = invoicesTableColumns;

    this.subscriptions.add(
      this.subscriptionApiService.getUpgradeSubscription().subscribe({
        next: (res) => {
          this.upgradesAvailable = res;
          this.nextUpgradeTitle = res[0]?.name;
        },
      }),
    );
    this.subscriptions.add(
      this.subscriptionApiService.getCurrentSubscription().subscribe({
        next: (res) => {
          this.currentSubscription = res;
        },
      }),
    );
  }

  ngAfterViewInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    if (this.columnsInitialized()) return;

    // Set templates on columns
    for (const column of this.columns) {
      if (column.prop === 'created') column.cellTemplate = this.dateCell;
      if (column.prop === 'amount') column.cellTemplate = this.amountCell;
      if (column.prop === 'actions') column.cellTemplate = this.actionsCell;
    }

    // Reassign array to trigger change detection in the table
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  getData(params: HttpParams): Observable<PaginatedResults<SubscriptionInvoice>> {
    return this.subscriptionApiService.getSubscriptionInvoices(params);
  }

  onDownloadInvoice(id: string) {
    this.subscriptionApiService.downloadSubscriptionInvoice(id).subscribe({
      next: () => {
        this.toastService.show('Invoice downloaded successfully.', 'iconoir-check-circle');
      },
    });
  }

  onManage() {
    this.offcanvasService.offCanvasNavigate('/your-plan/manage');
  }

  onEditBusiness() {
    this.offcanvasService.offCanvasNavigate(`/your-plan/edit-business-details/${this.businessDetails()?.id}`);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly SubscriptionStatus = SubscriptionStatus;
}
